import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { axiosInstance } from "../../utils/axios.js";
import { useLocation, useParams, useNavigate, NavLink } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, CheckCircle, Upload, Calendar, FileText, AlertTriangle, X, CreditCard, Shield, Clock, User } from 'lucide-react';
import './reserve.scss';

// マークダウン形式のリンク [テキスト](URL) をReact要素に変換する関数
const parseMarkdownLinks = (text) => {
    if (!text) return text;
    
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
        // リンク前のテキストを追加
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        
        // リンクを追加
        parts.push(
            <a 
                key={match.index}
                href={match[2]} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#3b82f6', textDecoration: 'underline' }}
            >
                {match[1]}
            </a>
        );
        
        lastIndex = match.index + match[0].length;
    }
    
    // 残りのテキストを追加
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
};

const DiagnoseContainer = (props) => {
    const [commonQuestions, setCommonQuestions] = useState([]);
    const [specificQuestions, setSpecificQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isNextDisabled, setIsNextDisabled] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [deadlineModal, setDeadlineModal] = useState(false);
    const [zipCodeErrorModal, setZipCodeErrorModal] = useState(false);
    const [zipCodeErrorMessage, setZipCodeErrorMessage] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [shiftInfo, setShiftInfo] = useState(null);

    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    useEffect(() => {
        // 診察情報を先に設定
        const adminUserId = location.state?.admin_user_id;
        const startTime = location.state?.start_time;
        
        // 問診情報を取得
        axiosInstance.get(`/reservations/diagnose?shift_id=${location.state.id}&subject_id=${location.state.subject_id}`, {
            headers: {
                'uid': props.token,
                'Access-Control-Allow-Origin': '*'
            }
        })
        .then(response => {
            const { common_questionnaire, specific_questionnaires = [] } = response.data;
            setCommonQuestions((common_questionnaire || []).filter(q => q.id !== 20 && q.id !== 21));
            setSpecificQuestions((specific_questionnaires.flat() || []).filter(q => q.id !== 20 && q.id !== 21));
            setIsLoading(false);
        })
        .catch(error => {
            console.error(error);
            setCommonQuestions([]);
            setSpecificQuestions([]);
            setIsLoading(false);
        });
    }, [id, location.state, props.token]);

    useEffect(() => {
        if (!isLoading) {
            validateCurrentPage();
        }
    }, [isLoading, currentQuestionIndex, answers, commonQuestions, specificQuestions]);

    const validateCurrentPage = () => {
        const currentQuestions = getCurrentQuestions();
        const isValid = currentQuestions.every(q => !q.required || (answers[q.id] !== undefined && answers[q.id] !== ''));
        setIsNextDisabled(!isValid);
    };

    const handleAnswerChange = (questionId, answer) => {
        setAnswers({ ...answers, [questionId]: answer });
    };
    
    const handleMultipleChoiceChange = (questionId, choiceId, checked) => {
        setAnswers(prevAnswers => {
            const currentAnswers = prevAnswers[questionId] || [];
            return {
                ...prevAnswers,
                [questionId]: checked 
                    ? [...currentAnswers, choiceId] 
                    : currentAnswers.filter(id => id !== choiceId)
            };
        });
    };

    const scrollToTop = () => {
        const sunnyContainer = document.querySelector('.sunny_container');
        if (sunnyContainer) {
            sunnyContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNext = () => {
        if (!isNextDisabled) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            setTimeout(scrollToTop, 50);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
            setTimeout(scrollToTop, 50);
        }
    };

    const handleZipCodeAutoFill = async (zipCode) => {
        const cleanZipCode = zipCode.replace(/-/g, '');
        
        if (cleanZipCode.length !== 7) {
            setZipCodeErrorMessage('郵便番号は7桁で入力してください');
            setZipCodeErrorModal(true);
            return;
        }
        
        try {
            const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${cleanZipCode}`);
            const data = await response.json();
            
            if (data.results && data.results.length > 0) {
                const result = data.results[0];
                const address = `${result.address1}${result.address2}${result.address3}`;
                handleAnswerChange(10, address);
            } else {
                setZipCodeErrorMessage('該当する住所が見つかりませんでした');
                setZipCodeErrorModal(true);
            }
        } catch (error) {
            console.error('住所の取得に失敗しました:', error);
            setZipCodeErrorMessage('住所の取得に失敗しました。しばらくしてから再度お試しください。');
            setZipCodeErrorModal(true);
        }
    };
    
    const getUnitForQuestion = (question) => {
        if (question.id === 7) return 'cm';
        if (question.id === 8) return 'kg';
        
        if (question.content) {
            if (question.content.includes('最高血圧') || question.content.includes('最低血圧')) {
                return 'mmHg';
            }
        }
        
        return '';
    };
    
    const shouldShowBloodPressureWarning = (question, value) => {
        if (!value || !question.content) return false;
        
        const numValue = parseInt(value);
        
        if (question.content.includes('最高血圧')) {
            return numValue >= 180;
        }
        
        if (question.content.includes('最低血圧')) {
            return numValue >= 120;
        }
        
        return false;
    };
    
    const getBloodPressureWarningMessage = (question) => {
        if (question.content.includes('最高血圧')) {
            return '180mmHg以上の方は精密検査や入院措置など緊急対応が必要な可能性があるため、対面の医療機関を受診してください。';
        }
        
        if (question.content.includes('最低血圧')) {
            return '120mmHg以上の方は精密検査や入院措置など緊急対応が必要な可能性があるため、対面の医療機関を受診してください。';
        }
        
        return '';
    };

    const getQuestionPlaceholder = (question) => {
        const placeholdersByQuestionId = {
            1: "山田太郎",
            2: "やまだたろう",
            3: "example@email.com",
            4: "09012345678(ハイフンなし）",
            5: "生年月日を選択してください",
            9: "1234567(ハイフンなし",
            10: "東京都渋谷区○○1-2-3",
            11: "〇〇マンション 101号室",
            14: "例：降圧薬（アムロジピン）、糖尿病薬（メトホルミン）など\n服用していない場合は「なし」と記入",
        };

        const contentBasedPlaceholders = {
            "氏名": "山田太郎",
            "ふりがな": "やまだたろう",
            "メールアドレス": "example@email.com",
            "電話番号": "09012345678(ハイフンなし）",
            "郵便番号": "1234567(ハイフンなし）",
            "住所": "都道府県から番地まで入力してください",
            "建物名": "マンション名・アパート名と部屋番号",
            "身長": "170（単位不要）",
            "体重": "65（単位不要）",
            "服薬中": "現在服用中のお薬をすべてご記入ください。ない場合は「なし」",
        };

        const typeBasedPlaceholders = {
            text: "詳しくご記入ください",
            sentence: "ご記入ください",
            number: "数値を入力してください",
            date: "日付を選択してください",
        };

        if (placeholdersByQuestionId[question.id]) {
            return placeholdersByQuestionId[question.id];
        }

        for (const [keyword, placeholder] of Object.entries(contentBasedPlaceholders)) {
            if (question.content && question.content.includes(keyword)) {
                return placeholder;
            }
        }

        return typeBasedPlaceholders[question.question_type] || "ご記入ください";
    };

    const CustomDateSelect = ({ questionId }) => {
        const existingValue = answers[questionId];
        const initialDate = existingValue ? new Date(existingValue) : new Date();
        const [year, setYear] = useState(initialDate.getUTCFullYear());
        const [month, setMonth] = useState(initialDate.getUTCMonth() + 1);
        const [day, setDay] = useState(initialDate.getUTCDate());
        const [isYearOpen, setIsYearOpen] = useState(false);
        const [isMonthOpen, setIsMonthOpen] = useState(false);
        const [isDayOpen, setIsDayOpen] = useState(false);
    
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
        const months = Array.from({ length: 12 }, (_, i) => i + 1);
        const daysInMonth = new Date(year, month, 0).getDate();
        const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    
        const updateDate = (y, m, d) => {
            const newDate = new Date(Date.UTC(y, m - 1, d, 13, 0, 0));
            handleAnswerChange(questionId, newDate.toISOString());
        };
    
        const handleYearSelect = (selectedYear) => {
            setYear(selectedYear);
            const validDay = Math.min(day, new Date(selectedYear, month, 0).getDate());
            setDay(validDay);
            updateDate(selectedYear, month, validDay);
            setIsYearOpen(false);
        };
    
        const handleMonthSelect = (selectedMonth) => {
            setMonth(selectedMonth);
            const validDay = Math.min(day, new Date(year, selectedMonth, 0).getDate());
            setDay(validDay);
            updateDate(year, selectedMonth, validDay);
            setIsMonthOpen(false);
        };
    
        const handleDaySelect = (selectedDay) => {
            setDay(selectedDay);
            updateDate(year, month, selectedDay);
            setIsDayOpen(false);
        };
    
        useEffect(() => {
            const handleClickOutside = (e) => {
                if (!e.target.closest('.custom-select-wrapper')) {
                    setIsYearOpen(false);
                    setIsMonthOpen(false);
                    setIsDayOpen(false);
                }
            };
            
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }, []);
    
        return (
            <div className="modern-date-selector">
                <div className="date-icon-wrapper">
                    <Calendar className="date-icon" />
                </div>
                
                <div className="date-selects-container">
                    <div className="custom-select-wrapper">
                        <div 
                            className={`custom-select-trigger ${isYearOpen ? 'active' : ''}`}
                            onClick={() => {
                                setIsYearOpen(!isYearOpen);
                                setIsMonthOpen(false);
                                setIsDayOpen(false);
                            }}
                        >
                            <span className="select-value">{year}</span>
                            <ChevronDown className="select-arrow" />
                        </div>
                        {isYearOpen && (
                            <div className="custom-select-dropdown year-dropdown">
                                {years.map(y => (
                                    <div 
                                        key={y} 
                                        className={`select-option ${y === year ? 'selected' : ''}`}
                                        onClick={() => handleYearSelect(y)}
                                    >
                                        {y}
                                    </div>
                                ))}
                            </div>
                        )}
                        <span className="date-label">年</span>
                    </div>
    
                    <div className="custom-select-wrapper">
                        <div 
                            className={`custom-select-trigger ${isMonthOpen ? 'active' : ''}`}
                            onClick={() => {
                                setIsMonthOpen(!isMonthOpen);
                                setIsYearOpen(false);
                                setIsDayOpen(false);
                            }}
                        >
                            <span className="select-value">{month}</span>
                            <ChevronDown className="select-arrow" />
                        </div>
                        {isMonthOpen && (
                            <div className="custom-select-dropdown month-dropdown">
                                {months.map(m => (
                                    <div 
                                        key={m} 
                                        className={`select-option ${m === month ? 'selected' : ''}`}
                                        onClick={() => handleMonthSelect(m)}
                                    >
                                        {m}
                                    </div>
                                ))}
                            </div>
                        )}
                        <span className="date-label">月</span>
                    </div>
    
                    <div className="custom-select-wrapper">
                        <div 
                            className={`custom-select-trigger ${isDayOpen ? 'active' : ''}`}
                            onClick={() => {
                                setIsDayOpen(!isDayOpen);
                                setIsYearOpen(false);
                                setIsMonthOpen(false);
                            }}
                        >
                            <span className="select-value">{day}</span>
                            <ChevronDown className="select-arrow" />
                        </div>
                        {isDayOpen && (
                            <div className="custom-select-dropdown day-dropdown">
                                {days.map(d => (
                                    <div 
                                        key={d} 
                                        className={`select-option ${d === day ? 'selected' : ''}`}
                                        onClick={() => handleDaySelect(d)}
                                    >
                                        {d}
                                    </div>
                                ))}
                            </div>
                        )}
                        <span className="date-label">日</span>
                    </div>
                </div>
            </div>
        );
    };

    const handleConfirmReservation = () => {
        setShowConfirmation(true);
        setTimeout(scrollToTop, 50);
    };

    const handleBackFromConfirmation = () => {
        setShowConfirmation(false);
        // 最後の質問ページに戻る
        const insuranceQuestions = [...commonQuestions, ...specificQuestions].filter(q => q.id === 20 || q.id === 21);
        const hasInsuranceQuestions = insuranceQuestions.length > 0;
        const lastQuestionIndex = initialCommonPages + commonQuestionsAfter3Pages.length + specificQuestionsFiltered.length + (hasInsuranceQuestions ? 0 : -1);
        setCurrentQuestionIndex(lastQuestionIndex);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        // start_timeフォーマット（例: "2025-04-03T23:40:00"）の場合
        if (dateString.includes('T')) {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
            const weekday = weekdays[date.getDay()];
            return `${year}年${month}月${day}日（${weekday}）`;
        }
        
        // 通常の日付フォーマット
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
        const weekday = weekdays[date.getDay()];
        return `${year}年${month}月${day}日（${weekday}）`;
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        
        // start_timeフォーマット（例: "2025-04-03T23:40:00"）の場合
        if (timeString.includes('T')) {
            const date = new Date(timeString);
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes}`;
        }
        
        // HH:MM:SS形式の場合
        return timeString.substring(0, 5);
    };

    const handleSubmit = () => {
        setIsLoading(true);
        const requestData = {
            answers: answers,
            questionnaire_id: 1,
            subject_id: location.state.subject_id,
        };
    
        const formData = new FormData();
        Object.keys(requestData.answers).forEach((key) => {
            if (typeof requestData.answers[key] === "object" && !(requestData.answers[key] instanceof File)) {
                formData.append(`answers[${key}]`, JSON.stringify(requestData.answers[key]));
            } else {
                formData.append(`answers[${key}]`, requestData.answers[key]);
            }
        });
        formData.append("subject_id", requestData.subject_id);
        Object.keys(answers).forEach((key) => {
            if (answers[key] instanceof File) {
                formData.append(`images[${key}]`, answers[key]);
            }
        });
    
        axiosInstance.post(`/reservations/create_through_diagnose/${id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'uid': props.token,
            },
            params: {
                shift_ids: location.state.shiftIds,
                subject_id: location.state.subject_id
              }
        })
        .then(res => {
            setIsLoading(false);
            if (res.data.message === '1') {
                setShowModal(true);
            } else if (res.data.message === '3') {
                setCancelModal(true);
            } else if (res.data.message === '4') {
                setDeadlineModal(true);
            } else {
                navigate('/mypage/reservation?status=success');
            }
        })
        .catch(error => console.error(error));
    };
    
    const handleImageUpload = (questionId, file) => {
        if (!file) return;
        setAnswers((prev) => ({
            ...prev,
            [questionId]: file,
        }));
    };      
    
    const isPhoneNumberQuestion = (question) => {
        return question.id === 4 || (question.content && question.content.includes('電話番号'));
    };

    const closeModal = () => {
        setShowModal(false);
        navigate('/mypage/reservation');
    };
    
    const closeModal_2 = () => {
        setCancelModal(false);
        navigate('/mypage/reservation');
    };

    const isInsuranceCardPage = () => {
        const currentQuestions = getCurrentQuestions();
        return currentQuestions.some(q => q.id === 20 || q.id === 21);
    };

    const getInsuranceCardQuestions = () => {
        const allQuestions = [...commonQuestions, ...specificQuestions];
        const frontQuestion = allQuestions.find(q => q.id === 20);
        const backQuestion = allQuestions.find(q => q.id === 21);
        return [frontQuestion, backQuestion].filter(Boolean);
    };
      
    const getCurrentQuestions = () => {
        const allQuestions = [...commonQuestions, ...specificQuestions];
        const insuranceQuestions = allQuestions.filter(q => q.id === 20 || q.id === 21);
        const nonInsuranceQuestions = allQuestions.filter(q => q.id !== 20 && q.id !== 21);

        const filteredQuestions = nonInsuranceQuestions;
        
        const initialCommonPages = Math.min(3, Math.ceil(filteredQuestions.filter(q => commonQuestions.includes(q)).length / 4));
        const commonQuestionsAfter3Pages = commonQuestions.filter(q => q.id !== 20 && q.id !== 21).slice(12);
        const specificQuestionsFiltered = specificQuestions.filter(q => q.id !== 20 && q.id !== 21);
        const nonInsuranceTotalPages = initialCommonPages + commonQuestionsAfter3Pages.length + specificQuestionsFiltered.length;
        
        if (currentQuestionIndex < initialCommonPages) {
            const startIndex = currentQuestionIndex * 4;
            return commonQuestions.filter(q => q.id !== 20 && q.id !== 21).slice(startIndex, startIndex + 4);
        } else if (currentQuestionIndex < initialCommonPages + commonQuestionsAfter3Pages.length) {
            const questionIndex = currentQuestionIndex - initialCommonPages;
            return [commonQuestionsAfter3Pages[questionIndex]];
        } else if (currentQuestionIndex < nonInsuranceTotalPages) {
            const specificIndex = currentQuestionIndex - initialCommonPages - commonQuestionsAfter3Pages.length;
            return specificQuestionsFiltered[specificIndex] ? [specificQuestionsFiltered[specificIndex]] : [];
        } else if (currentQuestionIndex === nonInsuranceTotalPages && insuranceQuestions.length > 0) {
            return insuranceQuestions;
        }
        
        return [];
    };
    
    const initialCommonPages = Math.min(3, Math.ceil(commonQuestions.filter(q => q.id !== 20 && q.id !== 21).length / 4));
    const commonQuestionsAfter3Pages = commonQuestions.filter(q => q.id !== 20 && q.id !== 21).slice(12);
    const specificQuestionsFiltered = specificQuestions.filter(q => q.id !== 20 && q.id !== 21);
    const insuranceQuestions = [...commonQuestions, ...specificQuestions].filter(q => q.id === 20 || q.id === 21);
    const hasInsuranceQuestions = insuranceQuestions.length > 0;
    const totalPages = initialCommonPages + commonQuestionsAfter3Pages.length + specificQuestionsFiltered.length + (hasInsuranceQuestions ? 1 : 0);
    const progressPercentage = ((currentQuestionIndex + 1) / totalPages) * 100;

    if (isLoading) {
        return (
          <div>
            <div className="loader" style={{ width: '100px', height: '100px' }}></div>
            <div
              style={{
                width: '100%',
                position: 'absolute',
                bottom: '0%',
                height: '80px',
                background: '#FEFBF9',
                zIndex: '12',
              }}
            />
          </div>
        );
    }

// DiagnoseContainer.js - 確認画面の表示部分を追加

// 確認画面の表示
if (showConfirmation) {
    return (
        <div className="sunny_container">
            <div className="diagnose_main_wrapper">
                <div className="reservation_confirmation_container">
                    <div className="confirmation_header">
                        <h2 className="confirmation_title">以下の内容で予約確定しますか？</h2>
                    </div>
                    
                    <div className="confirmation_content">
                        <div className="confirmation_section">
                            <div className="confirmation_card">
                                <h3 className="section_subtitle">ご予約内容</h3>
                                <div className="doctor_info">
                                    <div className="doctor_details">
                                    <h4 className="doctor_name">担当医: {location.state?.last_name}先生</h4>
                                        <p className="appointment_datetime">
                                            {formatDate(shiftInfo?.start_time || location.state?.start_time)}
                                            {formatTime(shiftInfo?.start_time || location.state?.start_time)}〜
                                        </p>
                                        <p className="appointment_duration">診察時間目安：約15分</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="payment_section">
                            <h3 className="section_subtitle">お支払い料金の目安</h3>
                            <div className="payment_card">
                                <div className="payment_row">
                                    <span className="payment_label">初診</span>
                                    <div>
                                        <span className="payment_amount" style={{fontSize:'22px',paddingRight:'6px'}}>2,300</span>
                                        <span className="payment_amount">円前後（税込）</span>
                                        <span className="payment_amount">+ お薬代</span>
                                    </div>
                                </div>
                                <div className="payment_notes">
                                        <p className="payment_note">お支払い方法はクレジットカード・銀行振込が利用できます。</p>
                                        <p className="payment_note">目安の金額になります。保険診療のため、診察内容に応じて料金が異なりますので、ご注意ください。</p>
                                        <p className="payment_note">薬代は処方日数や薬の種類によって値段が変動します。</p>
                                        <p className="payment_note">追加で血液検査キットをご利用の場合は自費負担（5,940円）となります。</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="confirmation_actions">
                        <button 
                            onClick={handleBackFromConfirmation}
                            className="confirmation_button back"
                        >
                            戻る
                        </button>
                        <button 
                            onClick={handleSubmit}
                            className="confirmation_button confirm analytics_conversion_reservation_first"
                        >
                            予約する
                        </button>
                    </div>
                    
                    {/* モーダル群 */}
                    {showModal && (
                        <div className="modal-overlay" onClick={closeModal}>
                            <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                                <span className="close-button" onClick={closeModal}>
                                    <X />
                                </span>
                                <div className="modal_icon warning_icon">
                                    <AlertTriangle />
                                </div>
                                <p className="modal_title">予約の競合が発生しました</p>
                                <p className="modal_subtitle">同じ時間の予約が先に埋まってしまいました。大変お手数ですが再度予約をお取りください。</p>
                            </div>
                        </div>
                    )}
                    
                    {cancelModal && (
                        <div className="modal-overlay" onClick={() => closeModal_2()}>
                            <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                                <span className="close-button" onClick={() => closeModal_2()}>
                                    <X />
                                </span>
                                <div className="modal_icon warning_icon">
                                    <AlertTriangle />
                                </div>
                                <p className="modal_title">予約の重複が検出されました</p>
                                <p className="modal_subtitle">お客様の予約が重複しております。すでに診察完了前の予約が入っておりますのでそちらをキャンセルもしくは予約時間変更してご利用ください。</p>
                            </div>
                        </div>
                    )}

                    {zipCodeErrorModal && (
                        <div className="modal-overlay" onClick={() => setZipCodeErrorModal(false)}>
                            <div className="modal-content info_modal" onClick={(e) => e.stopPropagation()}>
                                <span className="close-button" onClick={() => setZipCodeErrorModal(false)}>
                                    <X />
                                </span>
                                <div className="modal_icon info_icon">
                                    <AlertTriangle />
                                </div>
                                <p className="modal_title">郵便番号エラー</p>
                                <p className="modal_subtitle">{zipCodeErrorMessage}</p>
                                <button 
                                    className="modal_confirm_button"
                                    onClick={() => setZipCodeErrorModal(false)}
                                >
                                    閉じる
                                </button>
                            </div>
                        </div>
                    )}
                                    
                    {deadlineModal && (
                        <div className="modal-overlay" onClick={() => closeModal_2()}>
                            <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                                <span className="close-button" onClick={() => closeModal_2()}>
                                    <X />
                                </span>
                                <div className="modal_icon warning_icon">
                                    <AlertTriangle />
                                </div>
                                <p className="modal_title">予約締切時間を過ぎました</p>
                                <p className="modal_subtitle">予約締切時間が過ぎました。お手数ですが診察開始時間以内に再度ご予約をお取りください。</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

    return (
        <div className="sunny_container">
            <div className="diagnose_main_wrapper">
                {/* プログレスバー */}
                <div className="diagnose_progress_container">
                    <div className="diagnose_progress_wrapper">
                        <div className="diagnose_progress_bar">
                            <div 
                                className="diagnose_progress_fill"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>
                        <span className="diagnose_progress_text">
                            {currentQuestionIndex + 1} / {totalPages}
                        </span>
                    </div>
                </div>

                {/* 質問コンテンツ */}
                <div className="diagnose_questions_container">
                    {isInsuranceCardPage() && (
                        <NavLink 
                        to="https://grey-brow-5dc.notion.site/210a4be0fa7080cd954fefcb5e8f4931"
                        className="button_link"
                        style={{width:'48%'}}
                        >
                            <div className="diagnose_insurance_icon_wrapper" style={{display:'flex',color:'#3b82f6',fontWeight:'bold',textDecoration:'underline',justifyContent:'center',margin:'1rem',fontSize:'1rem',alignItems:'center'}}>
                                <CreditCard className="diagnose_insurance_main_icon" />
                                <p className="diagnose_insurance_shield_icon" style={{marginLeft:'4px'}}>マイナ保険証ご利用の方</p>
                            </div>
                    </NavLink>
                    )}
                    {getCurrentQuestions().length > 0 ? (
                        getCurrentQuestions().map((q) => (
                            <div 
                                key={q.id} 
                                className={`diagnose_question_card ${isInsuranceCardPage() ? 'insurance-card-question' : ''}`}
                            >
                                <div
                                  className="diagnose_question_header"
                                  style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                                >
                                    {isInsuranceCardPage() ? (
                                        <CreditCard className="diagnose_question_icon insurance-icon" />
                                    ) : (
                                        <FileText className="diagnose_question_icon" />
                                    )}
                                    <h3
                                      className={`diagnose_question_title ${isInsuranceCardPage() ? 'insurance-title' : ''}`}
                                      style={{ textAlign: 'left' }}
                                    >
                                        {parseMarkdownLinks(q.content)} 
                                        {q.required && <span className="diagnose_required_mark">※必須</span>}
                                    </h3>
                                </div>
                                
                                <div className="diagnose_question_content">
                                    {q.question_type === 'single_choice' && q.choices.map(choice => (
                                        <label
                                            key={choice.id}
                                            className={`diagnose_choice_option single ${answers[q.id] === choice.id ? 'selected' : ''}`}
                                            onClick={() => handleAnswerChange(q.id, choice.id)}
                                        >
                                            <input
                                                type="radio"
                                                name={`question-${q.id}`}
                                                value={choice.id}
                                                checked={answers[q.id] === choice.id}
                                                onChange={() => handleAnswerChange(q.id, choice.id)}
                                                className="diagnose_choice_input"
                                            />
                                            <div className="diagnose_choice_indicator">
                                                {answers[q.id] === choice.id && <CheckCircle className="diagnose_choice_check" />}
                                            </div>
                                            <span className="diagnose_choice_text">{choice.choice_text}</span>
                                        </label>
                                    ))}
                                    
                                    {q.question_type === 'multiple_choice' && q.choices.map(choice => (
                                        <label
                                            key={choice.id}
                                            className={`diagnose_choice_option multiple ${answers[q.id]?.includes(choice.id) ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                value={choice.id}
                                                checked={answers[q.id]?.includes(choice.id) || false}
                                                onChange={(e) => handleMultipleChoiceChange(q.id, choice.id, e.target.checked)}
                                                className="diagnose_choice_input"
                                            />
                                            <div className="diagnose_choice_indicator">
                                                {answers[q.id]?.includes(choice.id) && <CheckCircle className="diagnose_choice_check" />}
                                            </div>
                                            <span className="diagnose_choice_text">{choice.choice_text}</span>
                                        </label>
                                    ))}
                                    
                                    {q.question_type === 'text' && (
                                        <div className="diagnose_text_input_wrapper">
                                            <textarea
                                                value={answers[q.id] || ''}
                                                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                                className="diagnose_textarea"
                                                rows={8}
                                                placeholder={getQuestionPlaceholder(q)}
                                            />
                                        </div>
                                    )}
                                    
                                    {q.question_type === 'sentence' && q.id === 9 && (
                                        <div className="diagnose_zipcode_wrapper">
                                            <div className="diagnose_zipcode_input_group">
                                                <input
                                                    type="text"
                                                    value={answers[q.id] || ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^[0-9-]*$/.test(value)) {
                                                            handleAnswerChange(q.id, value);
                                                        }
                                                    }}
                                                    className="diagnose_zipcode_input"
                                                    placeholder={getQuestionPlaceholder(q)}
                                                    maxLength="8"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleZipCodeAutoFill(answers[q.id] || '')}
                                                    className="diagnose_zipcode_autofill_button"
                                                >
                                                    住所自動入力
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {q.question_type === 'sentence' && q.id !== 9 && (
                                        <div className="diagnose_text_input_wrapper">
                                            <FileText className="diagnose_text_icon" />
                                            <input
                                                type="text"
                                                value={answers[q.id] || ''}
                                                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                                className="diagnose_text_input_single"
                                                placeholder={getQuestionPlaceholder(q)}
                                            />
                                        </div>
                                    )}
                                    
                                    {q.question_type === 'number' && (
                                        <div className="diagnose_number_input_container">
                                            <div 
                                                className="diagnose_number_input_wrapper" 
                                                style={{width: isPhoneNumberQuestion(q) ? 'auto' : 'fit-content'}}
                                            >
                                                <FileText className="diagnose_text_icon" />
                                                <div className="diagnose_number_with_unit">
                                                    <input
                                                        type="number"
                                                        value={answers[q.id] || ''}
                                                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                                        className={isPhoneNumberQuestion(q) ? "diagnose_number_input_full" : "diagnose_number_input_short"}
                                                        placeholder={getQuestionPlaceholder(q)}
                                                        style={{textAlign: isPhoneNumberQuestion(q) ? 'left' : 'left'}}
                                                    />
                                                    {!isPhoneNumberQuestion(q) && (
                                                        <span className="diagnose_input_unit">
                                                            {getUnitForQuestion(q)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {shouldShowBloodPressureWarning(q, answers[q.id]) && (
                                                <div className="diagnose_blood_pressure_warning">
                                                    <AlertTriangle className="warning-icon" />
                                                    <p className="warning-text">
                                                        {getBloodPressureWarningMessage(q)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {q.question_type === 'date' && (
                                        <CustomDateSelect questionId={q.id} />
                                    )}
                                    
                                    {q.question_type === 'image_upload' && (
                                        <div className={`diagnose_image_upload_wrapper ${isInsuranceCardPage() ? 'insurance-upload' : ''}`}>
                                            <div
                                                className={`diagnose_image_upload_area ${isInsuranceCardPage() ? 'insurance-upload-area' : ''}`}
                                                onClick={() => document.getElementById(`file-input-${q.id}`).click()}
                                            >
                                                <input
                                                    id={`file-input-${q.id}`}
                                                    type="file"
                                                    accept="image/*,application/pdf"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => handleImageUpload(q.id, e.target.files[0])}
                                                />
                                                {answers[q.id] instanceof File ? (
                                                    <div className="diagnose_image_preview">
                                                        <img
                                                            src={URL.createObjectURL(answers[q.id])}
                                                            alt="preview"
                                                            className="diagnose_preview_image"
                                                        />
                                                        <div className="diagnose_image_overlay">
                                                            <CheckCircle className="diagnose_upload_success_icon" />
                                                            <span>画像がアップロードされました</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="diagnose_upload_placeholder">
                                                        <Upload className="diagnose_upload_icon" />
                                                        <p className="diagnose_upload_text">
                                                            {isInsuranceCardPage() ? 
                                                                `保険証の${q.id === 20 ? '表面' : '裏面'}もしくは資格確認画面を撮影してアップロード` :
                                                                'ここをクリックして画像を添付してください'
                                                            }
                                                        </p>
                                                        <p className="diagnose_upload_subtext">
                                                            JPG, PNG, PDF対応
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="diagnose_no_questions">
                            <AlertTriangle className="diagnose_no_questions_icon" />
                            <p>質問がありません。</p>
                        </div>
                    )}
                </div>

                {/* ナビゲーションボタン */}
                <div className="footer_content_page">
                    <div className="diagnose_navigation_buttons">
                        {currentQuestionIndex > 0 && (
                            <button 
                                onClick={handlePrevious} 
                                className="diagnose_nav_button previous"
                            >
                                <ChevronLeft className="diagnose_nav_icon" />
                                <span>戻る</span>
                            </button>
                        )}
                        {currentQuestionIndex < totalPages - 1 ? (
                            <button
                                onClick={handleNext}
                                className={`diagnose_nav_button next ${isNextDisabled ? 'disabled' : 'active'}`}
                                disabled={isNextDisabled}
                                style={{width:'20%'}}
                            >
                                <span>次へ進む</span>
                                <ChevronRight className="diagnose_nav_icon" />
                            </button>
                        ) : (
                            <button
                                onClick={handleConfirmReservation}
                                className={`diagnose_nav_button submit ${isNextDisabled ? 'disabled' : 'active'}`}
                                disabled={isNextDisabled}
                                style={{width:'80%'}}
                            >
                                <span>確認画面へ</span>
                                <ChevronRight className="diagnose_nav_icon" />
                            </button>
                        )}
                    </div>
                </div>
                
                {/* モーダル */}
                {showModal && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={closeModal}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約の競合が発生しました</p>
                            <p className="modal_subtitle">同じ時間の予約が先に埋まってしまいました。大変お手数ですが再度予約をお取りください。</p>
                        </div>
                    </div>
                )}
                
                {cancelModal && (
                    <div className="modal-overlay" onClick={() => closeModal_2()}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={() => closeModal_2()}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約の重複が検出されました</p>
                            <p className="modal_subtitle">お客様の予約が重複しております。すでに診察完了前の予約が入っておりますのでそちらをキャンセルもしくは予約時間変更してご利用ください。</p>
                        </div>
                    </div>
                )}

                {zipCodeErrorModal && (
                    <div className="modal-overlay" onClick={() => setZipCodeErrorModal(false)}>
                        <div className="modal-content info_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={() => setZipCodeErrorModal(false)}>
                                <X />
                            </span>
                            <div className="modal_icon info_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">郵便番号エラー</p>
                            <p className="modal_subtitle">{zipCodeErrorMessage}</p>
                            <button 
                                className="modal_confirm_button"
                                onClick={() => setZipCodeErrorModal(false)}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                )}
                                
                {deadlineModal && (
                    <div className="modal-overlay" onClick={() => closeModal_2()}>
                        <div className="modal-content warning_modal" onClick={(e) => e.stopPropagation()}>
                            <span className="close-button" onClick={() => closeModal_2()}>
                                <X />
                            </span>
                            <div className="modal_icon warning_icon">
                                <AlertTriangle />
                            </div>
                            <p className="modal_title">予約締切時間を過ぎました</p>
                            <p className="modal_subtitle">予約締切時間が過ぎました。お手数ですが診察開始時間以内に再度ご予約をお取りください。</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );    
};

export default DiagnoseContainer;